import React, { useEffect, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, Users, UserPlus, MailCheck, GraduationCap, Crown, Loader2 } from "lucide-react";
import client from "@/lib/api";

const RANGES = [7, 30, 90];

const TOTALS = [
  { key: "pageviews", label: "Pageviews", icon: TrendingUp, c: "text-violet-600 bg-violet-100" },
  { key: "visitors", label: "Visitors", icon: Users, c: "text-blue-600 bg-blue-100" },
  { key: "signups", label: "Signups", icon: UserPlus, c: "text-emerald-600 bg-emerald-100" },
  { key: "verifications", label: "Verified", icon: MailCheck, c: "text-sky-600 bg-sky-100" },
  { key: "lesson_completions", label: "Lessons Done", icon: GraduationCap, c: "text-indigo-600 bg-indigo-100" },
  { key: "pro_conversions", label: "PRO Conversions", icon: Crown, c: "text-amber-500 bg-amber-100" },
];

const Bar = ({ label, count, max }) => (
  <div>
    <div className="flex justify-between text-sm mb-1"><span className="font-medium text-slate-700">{label}</span><span className="text-slate-400">{count}</span></div>
    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full mel-gradient rounded-full" style={{ width: `${max ? (count / max) * 100 : 0}%` }} /></div>
  </div>
);

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get(`/admin/analytics/overview?days=${days}`).then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [days]);

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Analytics</h1>
          <p className="text-slate-500">First-party, privacy-conscious insights</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)} data-testid={`analytics-range-${r}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${days === r ? "mel-gradient text-white" : "text-slate-500"}`}>{r}d</button>
          ))}
        </div>
      </div>

      {loading && <div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-500" /></div>}

      {!loading && d && !d.has_data && (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="analytics-empty">
          <TrendingUp size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="font-heading font-bold text-lg">No historical data available</p>
          <p className="text-slate-500 text-sm mt-1">Real analytics are collected from now on. Data will appear here as people use MELLETIC.</p>
        </div>
      )}

      {!loading && d && d.has_data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {TOTALS.map((t) => (
              <div key={t.key} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`analytics-total-${t.key}`}>
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.c}`}><t.icon size={20} /></span>
                <div className="font-heading text-3xl font-extrabold mt-3">{d.totals?.[t.key] ?? 0}</div>
                <div className="text-sm text-slate-500 mt-0.5">{t.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6">
            <h3 className="font-heading font-bold mb-4">Traffic</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.traffic}>
                  <defs>
                    <linearGradient id="tv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7c3aed" stopOpacity={0.35} /><stop offset="100%" stopColor="#7c3aed" stopOpacity={0} /></linearGradient>
                    <linearGradient id="ts" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => (v || "").slice(5)} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} width={30} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} />
                  <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#7c3aed" strokeWidth={3} fill="url(#tv)" />
                  <Area type="monotone" dataKey="signups" name="Signups" stroke="#10b981" strokeWidth={2} fill="url(#ts)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="font-heading font-bold mb-4">Conversion Funnel</h3>
              <div className="space-y-3">
                {d.funnel.map((f) => <Bar key={f.step} label={f.step} count={f.count} max={Math.max(1, ...d.funnel.map((x) => x.count))} />)}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="font-heading font-bold mb-4">Retention</h3>
              <div className="flex items-center gap-6">
                <div className="relative w-28 h-28">
                  <svg viewBox="0 0 36 36" className="w-28 h-28 -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#7c3aed" strokeWidth="3" strokeDasharray={`${d.retention.retention_pct} 100`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center font-heading font-extrabold text-xl">{d.retention.retention_pct}%</div>
                </div>
                <div className="text-sm text-slate-600">
                  <p><b>{d.retention.returning}</b> of <b>{d.retention.cohort_size}</b> new users returned on a later day.</p>
                  <p className="text-slate-400 mt-1">Signup-cohort return rate for the selected period.</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
